import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useId, useState, type ReactElement } from 'react';
import type { ConnectionState } from '@chatapp-inbox/shared';
import { refreshLines } from '../../api';
import { Button } from '../details/Button';
import { StatusRegion } from '../details/StatusRegion';
import { useStatusMessage } from '../details/use-status-message';
import { connectionErrorMessage } from './error-message';
import { LineRow } from './LineRow';
import { describeRefresh } from './lines-text';
import { applyConnection } from './use-connection';
import forms from './forms.module.css';
import section from './Section.module.css';
import styles from './LinesSection.module.css';

/**
 * Os números da conexão. O administrador não cadastra número por número: o serviço de mensagens os
 * descobre, e aqui só se liga ou desliga cada um (desligado não recebe nem envia, mas as conversas
 * dele continuam legíveis). "Atualizar números" pede ao serviço que procure de novo.
 */
export function LinesSection({ connection }: { connection: ConnectionState }): ReactElement {
  const queryClient = useQueryClient();
  const headingId = useId();
  const status = useStatusMessage();
  const [error, setError] = useState<string | null>(null);
  const { lines, connected } = connection;

  const refresh = useMutation({
    mutationFn: refreshLines,
    onSuccess: (state) => applyConnection(queryClient, state),
  });

  const runRefresh = (): void => {
    if (refresh.isPending) return;
    setError(null);
    status.announce('');
    const before = lines;
    refresh.mutate(undefined, {
      onSuccess: (state) => status.announce(describeRefresh(before, state.lines)),
      onError: (failure) =>
        setError(
          connectionErrorMessage(failure, 'Não foi possível atualizar os números. Tente de novo.'),
        ),
    });
  };

  return (
    <section aria-labelledby={headingId} className={section.card}>
      <h3 id={headingId} className={section.cardTitle}>
        Números
      </h3>
      <p className={forms.hint}>
        Os números de WhatsApp desta conexão. Ligue os que a equipe usa para atender: um número
        desligado não recebe nem envia mensagens, mas as conversas dele continuam podendo ser lidas.
      </p>

      {/* Sem conexão não há onde procurar números. */}
      {connected && (
        <div className={forms.actions}>
          <Button busy={refresh.isPending} onClick={runRefresh}>
            {refresh.isPending ? 'Atualizando…' : 'Atualizar números'}
          </Button>
        </div>
      )}
      <StatusRegion message={status.message} />
      {error && (
        <p role="alert" className={forms.alert}>
          {error}
        </p>
      )}

      {lines.length === 0 ? (
        <p className={styles.empty}>
          Nenhum número encontrado ainda.{' '}
          {connected
            ? 'Use “Atualizar números” para procurar de novo; se continuar vazio, confira se há números no WhatsApp conectado.'
            : 'Salve a conexão acima para que os números sejam descobertos.'}
        </p>
      ) : (
        <ul aria-label="Números" className={styles.list}>
          {lines.map((line) => (
            <LineRow key={line.id} line={line} canRegister={connection.canRegisterLines} />
          ))}
        </ul>
      )}
    </section>
  );
}
